import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Flex,
  Heading,
  Text,
  Button,
  IconButton,
  Icon,
  Badge,
  Select,
  Switch,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  Tooltip,
  Alert,
  AlertIcon,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  Textarea,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Spinner,
  Center,
  useToast,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiDollarSign,
  FiDownload,
  FiEye,
  FiMail,
  FiCheckCircle,
  FiAlertTriangle,
  FiTrash2,
  FiRefreshCw,
} from 'react-icons/fi';
import { useHistory, Redirect } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import Papa from 'papaparse';
import { functionsUsEast1 } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCsvFile } from '../../utils/reachExport';

/**
 * Usage & Billing — admin-only management page for per-use integrations
 * (PostcardMania postcards today; future per-use services appear as tabs
 * automatically via the backend adapter registry).
 *
 * Per user + month: pieces sent, known cost (stamped at order time),
 * settlements recorded (cost recovery from LOs), and outstanding balance.
 * All data flows through admin-only callables (getUsageReport, getUsageDetail,
 * recordUsageSettlement, deleteUsageSettlement).
 */

const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** 'YYYY-MM' -> 'October 2026' */
function monthLabel(month) {
  const [y, m] = (month || '').split('-').map(Number);
  if (!y || !m) return month || '';
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** 'YYYY-MM' +/- 1 month */
function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function currentMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

const STATUS_COLORS = {
  Pending: 'yellow',
  'Pending Payment': 'orange',
  Processing: 'blue',
  Processed: 'blue',
  Mailing: 'teal',
  Delivered: 'green',
  Canceled: 'gray',
  Undeliverable: 'red',
};

function KpiCard({ label, value, subtext, icon, colorScheme = 'blue', loading, tooltipLabel }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  return (
    <Box p={5} shadow="sm" borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={bg}>
      <Stat>
        <Flex align="center" gap={2}>
          {icon && <Icon as={icon} color={`${colorScheme}.500`} boxSize={4} />}
          <StatLabel color="gray.500" fontSize="xs" fontWeight="bold" textTransform="uppercase">
            {label}
          </StatLabel>
          {tooltipLabel && (
            <Tooltip label={tooltipLabel} placement="top">
              <span><Icon as={FiAlertTriangle} boxSize={3} color="orange.400" /></span>
            </Tooltip>
          )}
        </Flex>
        <Skeleton isLoaded={!loading} minH="28px" mt={1}>
          <StatNumber fontSize="2xl" fontWeight="bold">{value}</StatNumber>
        </Skeleton>
        {subtext && <StatHelpText fontSize="xs" color="gray.400">{subtext}</StatHelpText>}
      </Stat>
    </Box>
  );
}

export default function UsageBillingPage() {
  // ----- All hooks unconditionally at the top (Rules of Hooks) -----
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const history = useHistory();
  const toast = useToast();

  const [integrationKey, setIntegrationKey] = useState('postcardmania');
  const [month, setMonth] = useState(currentMonth());
  const [includeSandbox, setIncludeSandbox] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sort, setSort] = useState({ key: 'units', dir: 'desc' });

  // Drill-down drawer
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const [detailUser, setDetailUser] = useState(null);
  const [detailOrders, setDetailOrders] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Settlement modal
  const { isOpen: isSettleOpen, onOpen: onSettleOpen, onClose: onSettleClose } = useDisclosure();
  const [settleUser, setSettleUser] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleNote, setSettleNote] = useState('');
  const [settleSaving, setSettleSaving] = useState(false);

  // Settlement delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const cancelDeleteRef = useRef();

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.800');
  const tableBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const rowHover = useColorModeValue('gray.50', 'gray.700');

  const isCRMAdmin = Boolean(
    SuperAdmin || Roles?.includes('CRM Admin') || currentUser?.CRMRole === 'CRM Admin'
  );

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'getUsageReport');
      const { data } = await fn({ integration: integrationKey, month, includeSandbox });
      setReport(data);
    } catch (err) {
      setLoadError(err.message || 'Could not load the usage report');
    } finally {
      setLoading(false);
    }
  }, [integrationKey, month, includeSandbox]);

  useEffect(() => {
    if (!isCRMAdmin) return;
    fetchReport();
  }, [isCRMAdmin, fetchReport]);

  const sortedUsers = useMemo(() => {
    const users = [...(report?.users || [])];
    const { key, dir } = sort;
    users.sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === 'asc' ? av - bv : bv - av;
    });
    return users;
  }, [report, sort]);

  const settlementsByUser = useMemo(() => {
    const map = new Map();
    for (const s of report?.settlements || []) {
      if (!map.has(s.userId)) map.set(s.userId, []);
      map.get(s.userId).push(s);
    }
    return map;
  }, [report]);

  // ----- Gate (after all hooks): CRM Admins / SuperAdmins only -----
  if (!isCRMAdmin) {
    return <Redirect to="/crm/dashboard" />;
  }

  const totals = report?.totals || {};
  const unitLabel = report?.integrations?.find((i) => i.key === integrationKey)?.unitLabel || 'units';

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  };
  const sortIndicator = (key) => (sort.key === key ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : '');

  const openDetail = async (user) => {
    setDetailUser(user);
    setDetailOrders([]);
    setDetailLoading(true);
    onDetailOpen();
    try {
      const fn = httpsCallable(functionsUsEast1, 'getUsageDetail');
      const { data } = await fn({ integration: integrationKey, month, userId: user.userId, includeSandbox });
      setDetailOrders(data?.orders || []);
    } catch (err) {
      toast({ title: 'Could not load orders', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDetailLoading(false);
    }
  };

  const openSettle = (user) => {
    setSettleUser(user);
    setSettleAmount(user.outstanding > 0 ? String(user.outstanding.toFixed(2)) : '');
    setSettleNote('');
    onSettleOpen();
  };

  const saveSettlement = async () => {
    const amount = Number(settleAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: 'Enter a positive amount', status: 'warning', duration: 3000 });
      return;
    }
    setSettleSaving(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'recordUsageSettlement');
      await fn({ integration: integrationKey, userId: settleUser.userId, month, amount, note: settleNote });
      toast({
        title: 'Settlement recorded',
        description: `${fmtMoney(amount)} recovered from ${settleUser.name} for ${monthLabel(month)}`,
        status: 'success',
        duration: 4000,
      });
      onSettleClose();
      await fetchReport();
    } catch (err) {
      toast({ title: 'Could not record settlement', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSettleSaving(false);
    }
  };

  const confirmDeleteSettlement = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'deleteUsageSettlement');
      await fn({ settlementId: deleteTarget.id });
      toast({ title: 'Settlement removed', status: 'success', duration: 3000 });
      setDeleteTarget(null);
      await fetchReport();
    } catch (err) {
      toast({ title: 'Could not delete settlement', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = () => {
    const rows = sortedUsers.map((u) => ({
      Month: month,
      Name: u.name,
      Email: u.email,
      Orders: u.orders,
      Pieces: u.units,
      Undeliverable: u.undeliverableUnits,
      'Known Cost': u.knownCost.toFixed(2),
      'Orders Missing Price': u.unknownCostOrders,
      Settled: u.settled.toFixed(2),
      Outstanding: u.outstanding.toFixed(2),
    }));
    downloadCsvFile(Papa.unparse(rows), `${integrationKey}-usage-${month}.csv`);
  };

  const integrationTabs = report?.integrations || [{ key: 'postcardmania', label: 'PostcardMania', unitLabel: 'postcards' }];
  const monthOptions = report?.months || [month];

  return (
    <Box bg={pageBg} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={5} maxW="1400px" mx="auto">
          {/* Header */}
          <Box bg={headerBg} p={5} borderRadius="xl" shadow="sm">
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <HStack spacing={3}>
                <Tooltip label="Back to Admin">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to Admin"
                    onClick={() => history.push('/crm/admin')}
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={0}>
                  <HStack spacing={2}>
                    <Icon as={FiDollarSign} boxSize={5} color="green.500" />
                    <Heading size="lg">Usage &amp; Billing</Heading>
                  </HStack>
                  <Text color="gray.500" fontSize="sm">
                    Per-user monthly usage and cost recovery for pay-per-use integrations
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3} wrap="wrap">
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="sandbox-toggle" mb={0} fontSize="sm" color="gray.500">
                    Include sandbox
                  </FormLabel>
                  <Switch
                    id="sandbox-toggle"
                    size="sm"
                    isChecked={includeSandbox}
                    onChange={(e) => setIncludeSandbox(e.target.checked)}
                  />
                </FormControl>
                <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={fetchReport} isLoading={loading}>
                  Refresh
                </Button>
                <Button size="sm" leftIcon={<FiDownload />} colorScheme="blue" onClick={exportCsv} isDisabled={!sortedUsers.length}>
                  Export CSV
                </Button>
              </HStack>
            </Flex>
          </Box>

          {/* Integration tabs + month picker */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Tabs
              variant="soft-rounded"
              colorScheme="green"
              index={Math.max(0, integrationTabs.findIndex((i) => i.key === integrationKey))}
              onChange={(idx) => setIntegrationKey(integrationTabs[idx]?.key || 'postcardmania')}
            >
              <TabList>
                {integrationTabs.map((i) => (
                  <Tab key={i.key}>{i.label}</Tab>
                ))}
              </TabList>
            </Tabs>

            <HStack spacing={1}>
              <IconButton
                icon={<FiChevronLeft />}
                aria-label="Previous month"
                size="sm"
                variant="ghost"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
              />
              <Select size="sm" w="190px" value={month} onChange={(e) => setMonth(e.target.value)}>
                {(monthOptions.includes(month) ? monthOptions : [month, ...monthOptions]).map((m) => (
                  <option key={m} value={m}>{monthLabel(m)}</option>
                ))}
              </Select>
              <IconButton
                icon={<FiChevronRight />}
                aria-label="Next month"
                size="sm"
                variant="ghost"
                isDisabled={month >= currentMonth()}
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
              />
            </HStack>
          </Flex>

          {includeSandbox && (
            <Alert status="warning" borderRadius="md" fontSize="sm" py={2}>
              <AlertIcon />
              Sandbox orders are test data — they are never printed, mailed, or billed. Exclude them for real accounting.
            </Alert>
          )}

          {loadError && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {loadError}
            </Alert>
          )}

          {/* KPIs */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <KpiCard
              label={`${unitLabel} sent`}
              value={loading ? '…' : (totals.units ?? 0).toLocaleString()}
              subtext={`${totals.orders ?? 0} order${(totals.orders ?? 0) === 1 ? '' : 's'}${totals.undeliverableUnits ? ` · ${totals.undeliverableUnits} undeliverable` : ''}`}
              icon={FiMail}
              colorScheme="blue"
              loading={loading}
            />
            <KpiCard
              label="Known cost"
              value={loading ? '…' : fmtMoney(totals.knownCost ?? 0)}
              subtext={totals.unknownCostOrders ? `${totals.unknownCostOrders} order(s) missing price` : 'all orders priced'}
              icon={FiDollarSign}
              colorScheme="purple"
              loading={loading}
              tooltipLabel={totals.unknownCostOrders ? 'Some orders have no stamped price (lookup unavailable at send time) — true cost is higher than shown.' : undefined}
            />
            <KpiCard
              label="Settled"
              value={loading ? '…' : fmtMoney(totals.settled ?? 0)}
              subtext="recovered from users"
              icon={FiCheckCircle}
              colorScheme="green"
              loading={loading}
            />
            <KpiCard
              label="Outstanding"
              value={loading ? '…' : fmtMoney(totals.outstanding ?? 0)}
              subtext="known cost − settled"
              icon={FiAlertTriangle}
              colorScheme={(totals.outstanding ?? 0) > 0 ? 'orange' : 'gray'}
              loading={loading}
            />
          </SimpleGrid>

          {/* Per-user table */}
          <Box bg={tableBg} borderRadius="xl" shadow="sm" borderWidth="1px" borderColor={borderColor} overflow="hidden">
            {loading ? (
              <Center py={16}><Spinner size="lg" color="blue.500" thickness="3px" /></Center>
            ) : !sortedUsers.length ? (
              <Center py={16} flexDir="column" gap={2}>
                <Icon as={FiMail} boxSize={8} color="gray.300" />
                <Text color="gray.500">No {unitLabel} {includeSandbox ? '' : '(production) '}for {monthLabel(month)}.</Text>
              </Center>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>User</Th>
                      <Th isNumeric>Orders</Th>
                      <Th isNumeric cursor="pointer" onClick={() => toggleSort('units')}>
                        Pieces{sortIndicator('units')}
                      </Th>
                      <Th isNumeric>Undeliverable</Th>
                      <Th isNumeric cursor="pointer" onClick={() => toggleSort('knownCost')}>
                        Est. Cost{sortIndicator('knownCost')}
                      </Th>
                      <Th isNumeric>Settled</Th>
                      <Th isNumeric cursor="pointer" onClick={() => toggleSort('outstanding')}>
                        Outstanding{sortIndicator('outstanding')}
                      </Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedUsers.map((u) => (
                      <Tr key={u.userId} _hover={{ bg: rowHover }}>
                        <Td>
                          <Text fontWeight="medium" fontSize="sm">{u.name}</Text>
                          <Text fontSize="xs" color="gray.500">{u.email}</Text>
                        </Td>
                        <Td isNumeric>
                          {u.orders}
                          {u.canceledOrders > 0 && (
                            <Tooltip label={`${u.canceledOrders} canceled order(s) excluded`}>
                              <Badge ml={1} colorScheme="gray" fontSize="2xs">+{u.canceledOrders}c</Badge>
                            </Tooltip>
                          )}
                        </Td>
                        <Td isNumeric fontWeight="semibold">{u.units.toLocaleString()}</Td>
                        <Td isNumeric>
                          {u.undeliverableUnits > 0 ? (
                            <Text as="span" color="red.500">{u.undeliverableUnits}</Text>
                          ) : '0'}
                        </Td>
                        <Td isNumeric>
                          {fmtMoney(u.knownCost)}
                          {u.unknownCostOrders > 0 && (
                            <Tooltip label={`${u.unknownCostOrders} order(s) missing a stamped price — true cost is higher`}>
                              <Icon as={FiAlertTriangle} ml={1} boxSize={3} color="orange.400" />
                            </Tooltip>
                          )}
                        </Td>
                        <Td isNumeric color={u.settled > 0 ? 'green.500' : undefined}>{fmtMoney(u.settled)}</Td>
                        <Td isNumeric>
                          <Text as="span" fontWeight="bold" color={u.outstanding > 0 ? 'orange.500' : u.outstanding < 0 ? 'blue.500' : 'green.500'}>
                            {fmtMoney(u.outstanding)}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="View orders">
                              <IconButton
                                icon={<FiEye />}
                                aria-label="View orders"
                                size="xs"
                                variant="ghost"
                                onClick={() => openDetail(u)}
                              />
                            </Tooltip>
                            <Button size="xs" colorScheme="green" variant="outline" leftIcon={<FiDollarSign />} onClick={() => openSettle(u)}>
                              Settle
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Order drill-down */}
      <Drawer isOpen={isDetailOpen} onClose={onDetailClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {detailUser?.name} — {monthLabel(month)}
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              {detailUser?.units.toLocaleString()} {unitLabel} · {fmtMoney(detailUser?.knownCost)}
            </Text>
          </DrawerHeader>
          <DrawerBody pb={6}>
            {detailLoading ? (
              <Center py={10}><Spinner /></Center>
            ) : !detailOrders.length ? (
              <Text color="gray.500" fontSize="sm">No orders for this month.</Text>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Date</Th>
                      <Th>Source</Th>
                      <Th>Design</Th>
                      <Th isNumeric>Qty</Th>
                      <Th isNumeric>Unit</Th>
                      <Th isNumeric>Total</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {detailOrders.map((o) => (
                      <Tr key={o.id} opacity={o.status === 'Canceled' ? 0.5 : 1}>
                        <Td fontSize="xs" whiteSpace="nowrap">{fmtDateTime(o.createdAt)}</Td>
                        <Td>
                          <Badge colorScheme={o.source === 'automation' ? 'purple' : 'blue'} fontSize="2xs">
                            {o.source}
                          </Badge>
                          {o.environment === 'sandbox' && (
                            <Badge ml={1} colorScheme="orange" fontSize="2xs">sandbox</Badge>
                          )}
                        </Td>
                        <Td fontSize="xs" maxW="160px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          <Tooltip label={`${o.designName || `#${o.designId}`} · ${o.mailClass}`}>
                            <Text as="span">{o.designName || `#${o.designId}`}</Text>
                          </Tooltip>
                        </Td>
                        <Td isNumeric>{o.units}</Td>
                        <Td isNumeric fontSize="xs">{o.unitPrice != null ? `$${o.unitPrice.toFixed(3)}` : '—'}</Td>
                        <Td isNumeric fontSize="xs" textDecoration={o.status === 'Canceled' ? 'line-through' : 'none'}>
                          {fmtMoney(o.totalCost)}
                        </Td>
                        <Td>
                          <Badge colorScheme={STATUS_COLORS[o.status] || 'gray'} fontSize="2xs">{o.status}</Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Settlement modal */}
      <Modal isOpen={isSettleOpen} onClose={onSettleClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Record settlement — {settleUser?.name}
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              {monthLabel(month)} · outstanding {fmtMoney(settleUser?.outstanding)}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Amount recovered ($)</FormLabel>
                <NumberInput min={0.01} precision={2} value={settleAmount} onChange={(v) => setSettleAmount(v)}>
                  <NumberInputField placeholder="0.00" />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Note</FormLabel>
                <Textarea
                  size="sm"
                  rows={2}
                  placeholder='e.g. "Deducted from commission — loan #1234 closed 10/3"'
                  value={settleNote}
                  onChange={(e) => setSettleNote(e.target.value)}
                />
              </FormControl>

              {(settlementsByUser.get(settleUser?.userId) || []).length > 0 && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                    Settlements this month
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {(settlementsByUser.get(settleUser?.userId) || []).map((s) => (
                      <Flex key={s.id} justify="space-between" align="center" fontSize="sm" borderWidth="1px" borderColor={borderColor} borderRadius="md" px={3} py={2}>
                        <Box>
                          <Text fontWeight="semibold">{fmtMoney(s.amount)}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {fmtDate(s.createdAt)} by {s.settledByName}{s.note ? ` — ${s.note}` : ''}
                          </Text>
                        </Box>
                        <IconButton
                          icon={<FiTrash2 />}
                          aria-label="Delete settlement"
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => setDeleteTarget(s)}
                        />
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSettleClose}>Cancel</Button>
            <Button colorScheme="green" onClick={saveSettlement} isLoading={settleSaving}>
              Record Settlement
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete settlement confirm */}
      <AlertDialog
        isOpen={!!deleteTarget}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setDeleteTarget(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg">Delete settlement?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Remove the {fmtMoney(deleteTarget?.amount)} settlement recorded {fmtDate(deleteTarget?.createdAt)}?
              The user&apos;s outstanding balance will go back up.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button colorScheme="red" size="sm" ml={3} onClick={confirmDeleteSettlement} isLoading={deleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
